import { Injectable } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';

import { cloneDeep } from 'lodash';
import { BehaviorSubject, EMPTY, finalize, Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { v4 as UUID } from 'uuid';

import { Dashboard, Project, Role, Widget } from '../../models';
import { WidgetMetricRule } from '../../models/widget-metric-rule';
import { AllSelection, SNACKBAR_CONSTANTS, WIDGET_SIZES } from '../../shared/constants';
import { DashboardAccessStatus, WidgetType } from '../../shared/enums';
import { DifferenceLogicUtility } from '../../shared/utils';
import { DashboardsDataService } from '../data-services';
import { DashboardsLogicServiceModel, Field } from './models';

@Injectable({
    providedIn: 'root'
})
export class DashboardsLogicService {
    private _areDashboardsLoaded = new BehaviorSubject<boolean>(false);
    private _currentDashboardChanged$ = new Subject<void>();
    private _currentDashboardUpdated$ = new Subject<void>();

    model: DashboardsLogicServiceModel = new DashboardsLogicServiceModel();

    areDashboardsLoaded$ = this._areDashboardsLoaded.asObservable();
    currentDashboardChanged$ = this._currentDashboardChanged$.asObservable();
    currentDashboardUpdated$ = this._currentDashboardUpdated$.asObservable();

    get selectedRoleIds(): string[] {
        const selectedRoleIds = this.model.dashboardForm.get('permissionRoleIds')?.value ?? [];
        return selectedRoleIds.map((id: string) => String(id));
    }

    get selectedReportWidgetColumnIds(): string[] {
        const selectedColumnIds = this.model.widgetForm.get('columnIds')?.value ?? [];
        return Array.isArray(selectedColumnIds) ? selectedColumnIds.map((id: string) => String(id)) : selectedColumnIds ? [String(selectedColumnIds)] : [];
    }

    get selectedProjectNoteWidgetIds(): string[] {
        const selectedIds = this.model.widgetForm.get('projectNoteIds')?.value ?? [];
        return selectedIds.map((id: string) => String(id));
    }

    get widgetMetricRulesFormArray(): UntypedFormArray {
        return this.model.widgetForm.get('widgetMetricRules') as UntypedFormArray;
    }

    constructor(
        private _dashboardsDataService: DashboardsDataService,
        private _snackbar: MatSnackBar,
    ) { }

    createDashboard(appId: string, projectId: string): Observable<boolean> {
        const isDashboardSaved = new Subject<boolean>();

        const dashboardToCreate = this.model.dashboardForm.value as Dashboard;
        dashboardToCreate.permissionRoleIds = dashboardToCreate.permissionRoleIds.filter((id: string) => id !== AllSelection.ALL);
        this._dashboardsDataService.createDashboard(appId, projectId, dashboardToCreate)
            .pipe(finalize(() => {
                isDashboardSaved.complete();
            }))
            .subscribe({
                next: (dashboard: Dashboard) => {
                    this.updateLocalDashboards(dashboard);
                    this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS('Dashboard', 'created'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                    isDashboardSaved.next(true);
                },
                error: () => {
                    this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Dashboard', 'creating'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                    isDashboardSaved.next(false);
                    isDashboardSaved.error(new Error('Error creating dashboard'));
                }
            });

        return isDashboardSaved.asObservable();
    }

    getDashboardsByProject(appId: string, projectId: string): void {
        if (this.model.areDashboardsLoading) {
            return;
        }

        this.model.areDashboardsLoading = true;
        this._dashboardsDataService.getDashboardsByProject(appId, projectId)
            .pipe(
                map((dashboards: Dashboard[]) => dashboards.map(d => this.addWidgetDefaults(d))),
                finalize(() => this.model.areDashboardsLoading = false))
            .subscribe({
                next: (dashboards: Dashboard[]) => {
                    this.model.dashboards = dashboards;
                    this.model.projectId = projectId;
                    this._areDashboardsLoaded.next(true);
                    this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS('Dashboards', 'loaded'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                },
                error: () => {
                    this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Dashboards', 'loading'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }
            });
    }

    getDashboardsByProjectByPermissions(appId: string, project: Project, userId: string): Observable<Dashboard[]> {
        if (this.model.areDashboardsLoading) {
            return EMPTY;
        }

        const projectId = project.id;
        this.model.areDashboardsLoading = true;
        this._areDashboardsLoaded.next(false);
        const dashboards$ = project.isSummaryProject
            ? this._dashboardsDataService.getSummaryDashboardsByPermissions(appId, projectId)
            : this._dashboardsDataService.getDashboardsByProjectByPermissions(appId, projectId, userId);
        return dashboards$
            .pipe(
                map((dashboards: Dashboard[]) => dashboards.map(d => this.addWidgetDefaults(d))),
                finalize(() => this.model.areDashboardsLoading = false),
                tap({
                    next: (dashboards: Dashboard[]) => {
                        this.model.dashboards = dashboards;
                        this.model.projectId = projectId;
                        this._areDashboardsLoaded.next(true);
                    },
                    error: () => {
                        this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Dashboards', 'loading'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                    }
                })
            );
    }

    getDashboardAccessStatus(appId: string, project: Project, dashboardId: string): Observable<DashboardAccessStatus> {
        return project.isSummaryProject
            ? this._dashboardsDataService.getSummaryDashboardAccessStatus(appId, project.id, dashboardId)
            : this._dashboardsDataService.getDashboardAccessStatus(appId, project.id, dashboardId);
    }

    updateDashboard(appId: string, projectId: string): Observable<boolean> {
        const isDashboardSaved = new Subject<boolean>();

        const newDashboardValues = this.model.dashboardForm.value as Dashboard;
        newDashboardValues.permissionRoleIds = newDashboardValues.permissionRoleIds.filter((id: string) => id !== AllSelection.ALL);
        const dashboardToUpdate = this.updatedDashboardWithFormValues(newDashboardValues);
        if (!dashboardToUpdate) {
            isDashboardSaved.next(false);
            isDashboardSaved.error(new Error('Error updating dashboard'));
            return isDashboardSaved.asObservable();
        }
        this._dashboardsDataService.updateDashboard(appId, projectId, dashboardToUpdate)
            .pipe(
                map((dashboard: Dashboard) => this.addWidgetDefaults(dashboard)),
                finalize(() => {
                    isDashboardSaved.complete();
                }))
            .subscribe({
                next: (dashboard: Dashboard) => {
                    if (!dashboard) {
                        this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Dashboard', 'updating'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                        isDashboardSaved.error(new Error('Error updating dashboard'));
                        return;
                    }
                    this.updateLocalDashboards(dashboard);
                    isDashboardSaved.next(true);
                    this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS('Dashboard', 'updated'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                },
                error: () => {
                    this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Dashboard', 'updating'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                    isDashboardSaved.next(false);
                    isDashboardSaved.error(new Error('Error updating dashboard'));
                }
            });

        return isDashboardSaved.asObservable();
    }

    saveCurrentDashboard(appId: string, projectId: string): void {
        if (!this.model.currentDashboard) {
            return;
        }

        this._dashboardsDataService.updateDashboard(appId, projectId, this.model.currentDashboard!)
            .pipe(map((dashboard: Dashboard) => this.addWidgetDefaults(dashboard)))
            .subscribe({
                next: (dashboard: Dashboard) => {
                    if (!dashboard) {
                        this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Dashboard', 'updating'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                        return;
                    }
                    this.updateLocalDashboards(dashboard);
                    this.setCurrentDashboardBeforeEditState();
                    this.resetChangesMadeToCurrentDashboard();
                    this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS('Dashboard', 'updated'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                },
                error: () => {
                    this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Dashboard', 'updating'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }
            });
    }

    updateDashboardActiveStatus(appId: string, projectId: string, dashboard: Dashboard, newActiveStatus: boolean): void {
        this.setDashboardActivatingState(dashboard, true);

        this._dashboardsDataService.updateDashboardActiveStatus(appId, projectId, dashboard, newActiveStatus)
            .pipe(
                map((dashboard: Dashboard) => this.addWidgetDefaults(dashboard)),
                finalize(() => {
                    this.setDashboardActivatingState(dashboard, false);
                })
            )
            .subscribe({
                next: (updatedDashboard: Dashboard) => {
                    if (!updatedDashboard) {
                        dashboard.active = !newActiveStatus;
                        this.updateLocalDashboards(dashboard);
                        this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Dashboard', 'updating'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                        return;
                    }
                    this.updateLocalDashboards(updatedDashboard);
                    this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_TOGGLE_ACTIVE('Dashboard', dashboard.name, newActiveStatus), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                },
                error: () => {
                    dashboard.active = !newActiveStatus;
                    this.updateLocalDashboards(dashboard);
                    this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Dashboard', 'updating'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }
            });
    }

    setDashboardActivatingState(dashboard: Dashboard, isActivating: boolean): void {
        if (isActivating) {
            this.model.dashboardsActivating.add(dashboard.id);
        }
        else {
            this.model.dashboardsActivating.delete(dashboard.id);
        }
    }

    deleteDashboard(appId: string, projectId: string, dashboardId: string): void {
        this._dashboardsDataService.deleteDashboard(appId, projectId, dashboardId)
            .subscribe({
                next: (success: boolean) => {
                    if (success) {
                        this.model.dashboards = this.model.dashboards.filter(d => d.id !== dashboardId);
                        this._areDashboardsLoaded.next(true);
                        this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS('Dashboard', 'deleted'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                    }
                    else {
                        this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Dashboard', 'deleting'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                    }
                },
                error: () => {
                    this._snackbar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Dashboard', 'deleting'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }
            });
    }

    setCurrentDashboard(dashboardId: string): void {
        this.model.currentDashboard = this.model.dashboards.find(d => d.id === dashboardId);
        this._currentDashboardChanged$.next();
    }

    clearCurrentDashboard(): void {
        this.model.currentDashboard = undefined;
        this.model.currentDashboardBeforeEditState = undefined;
        this._currentDashboardChanged$.next();
    }

    updateLocalDashboards(updatedDashboard: Dashboard): void {
        const index = this.model.dashboards.findIndex(d => d.id === updatedDashboard.id);
        if (index !== -1) {
            this.model.dashboards[index] = updatedDashboard;
        }
        else {
            this.model.dashboards.push(updatedDashboard);
        }

        this._areDashboardsLoaded.next(true);
    }

    initNewDashboard(projectId: string): void {
        this.model.dashboardForm = new UntypedFormGroup({
            id: new UntypedFormControl(''),
            projectId: new UntypedFormControl(projectId),
            name: new UntypedFormControl('', [Validators.required]),
            description: new UntypedFormControl(''),
            permissionRoleIds: new UntypedFormControl([]),
            permissionUserIds: new UntypedFormControl([]),
            allowAllUsersAccess: new UntypedFormControl(false),
        });
    }

    editingExistingDashboardInit(existingDashboardToEdit: Dashboard, projectRoles: Role[]): void {
        this.model.dashboardForm = new UntypedFormGroup({
            id: new UntypedFormControl(existingDashboardToEdit.id),
            projectId: new UntypedFormControl(existingDashboardToEdit.projectId),
            name: new UntypedFormControl(existingDashboardToEdit.name, [Validators.required]),
            description: new UntypedFormControl(existingDashboardToEdit.description),
            permissionRoleIds: new UntypedFormControl(existingDashboardToEdit.permissionRoleIds),
            permissionUserIds: new UntypedFormControl(existingDashboardToEdit.permissionUserIds),
            allowAllUsersAccess: new UntypedFormControl(existingDashboardToEdit.allowAllUsersAccess ?? false),
        });

        this.syncAllRolesSelectionState(projectRoles);
    }

    initNewWidget(): void {
        this.model.isEditingExistingWidget = false;
        this.model.widgetForm = new UntypedFormGroup({
            id: new UntypedFormControl(UUID()),
            type: new UntypedFormControl('', [Validators.required]),
            isTypeSelected: new UntypedFormControl(false, [Validators.required]),
            name: new UntypedFormControl('', [Validators.required]),
            description: new UntypedFormControl(''),
            reportId: new UntypedFormControl(null),
            reportViewId: new UntypedFormControl(null),
            columnIds: new UntypedFormControl(null),
            projectNoteIds: new UntypedFormControl(null),
            widgetMetricRules: new UntypedFormArray([]),
        });
    }

    editExistingWidget(widget: Widget): void {
        this.model.isEditingExistingWidget = true;
        this.model.widgetForm = new UntypedFormGroup({
            id: new UntypedFormControl(widget.id),
            type: new UntypedFormControl(widget.type, [Validators.required]),
            isTypeSelected: new UntypedFormControl(true, [Validators.required]),
            name: new UntypedFormControl(widget.name, [Validators.required]),
            description: new UntypedFormControl(widget.description),
            reportId: new UntypedFormControl(widget.reportId),
            reportViewId: new UntypedFormControl(widget.reportViewId),
            columnIds: new UntypedFormControl(this.isSingleSelectColumnWidget(widget.type) ? (widget.columnIds?.[0] ?? null) : (widget.columnIds ?? [])),
            projectNoteIds: new UntypedFormControl(widget.projectNoteIds),
            widgetMetricRules: new UntypedFormArray(
                (widget.widgetMetricRules ?? []).map(rule => this.buildMetricRuleGroup(rule))
            ),
        });
        this.applyWidgetTypeValidators(widget.type);
    }

    deleteWidgetFromCurrentDashboard(widget: Widget): void {
        this.model.currentDashboard!.widgets = this.model.currentDashboard!.widgets.filter(w => w.id !== widget.id);
        this.validateChangesMadeToCurrentDashboard();
        this._currentDashboardUpdated$.next();
    }

    setWidgetType(): void {
        this.model.widgetForm.get('isTypeSelected')?.setValue(true);
        const selectedType = this.model.widgetForm.get('type')?.value;
        this.applyWidgetTypeValidators(selectedType);
    }

    private applyWidgetTypeValidators(type: string): void {
        switch (type) {
            case WidgetType.Report:
                this.setReportFieldsValidators();
                break;
            case WidgetType.ProjectNote:
                this.setProjectNoteWidgetValidators();
                break;
            case WidgetType.Pie:
                this.setReportFieldsValidators();
                break;
            case WidgetType.Donut:
                this.setReportFieldsValidators();
                break;
            case WidgetType.Number:
                this.setReportFieldsValidators();
                this.setNumberWidgetMetricRulesValidators();
                break;
            default:
                break;
        }
    }

    private setNumberWidgetMetricRulesValidators(): void {
        this.widgetMetricRulesFormArray.setValidators([Validators.required]);

        this.widgetMetricRulesFormArray.controls.forEach(control => {
            const metricRuleGroup = control as UntypedFormGroup;
            const operator = metricRuleGroup.get('operator');
            const aggregate = metricRuleGroup.get('aggregate');

            operator?.setValidators([Validators.required]);
            aggregate?.setValidators([Validators.required]);

            operator?.updateValueAndValidity();
            aggregate?.updateValueAndValidity();
        });

        this.widgetMetricRulesFormArray.updateValueAndValidity();
    }

    private setReportFieldsValidators(): void {
        const reportId = this.model.widgetForm.get('reportId');
        const reportViewId = this.model.widgetForm.get('reportViewId');
        const columnIds = this.model.widgetForm.get('columnIds');

        reportId?.setValidators([Validators.required]);
        reportViewId?.setValidators([Validators.required]);
        columnIds?.setValidators([Validators.required]);

        reportId?.updateValueAndValidity();
        reportViewId?.updateValueAndValidity();
        columnIds?.updateValueAndValidity();
    }

    clearReportViewValidator(): void {
        const reportViewId = this.model.widgetForm.get('reportViewId');
        reportViewId?.setValue(null);
        reportViewId?.clearValidators();
        reportViewId?.updateValueAndValidity();
    }

    addUpdateWidgetToDashboard(): void {
        if (!this.model.currentDashboard) {
            return;
        }

        if (this.model.widgetForm.value.type === WidgetType.Number) {
            (this.widgetMetricRulesFormArray.at(0) as UntypedFormGroup)?.get('aggregate')?.enable();
        }
        const formValue = this.model.widgetForm.value;
        const columnIds = this.isSingleSelectColumnWidget(formValue.type)
            ? (formValue.columnIds ? [formValue.columnIds] : [])
            : (formValue.columnIds ?? []);
        const widgetToUpdateInDashboard = {
            ...formValue,
            columnIds,
        } as Widget;

        if (this.model.isEditingExistingWidget) {
            const widgetIndex = this.model.currentDashboard.widgets?.findIndex(widget => widget.id === this.model.widgetForm.get('id')?.value) ?? -1;
            if (widgetIndex !== -1) {
                widgetToUpdateInDashboard.w = this.model.currentDashboard.widgets![widgetIndex].w;
                widgetToUpdateInDashboard.h = this.model.currentDashboard.widgets![widgetIndex].h;
                this.model.currentDashboard.widgets![widgetIndex] = widgetToUpdateInDashboard;
            }
        }
        else {
            if (!this.model.currentDashboard?.widgets) {
                this.model.currentDashboard.widgets = [];
            }
            this.addWidgetTypeSizeDefault(widgetToUpdateInDashboard);
            this.model.currentDashboard?.widgets.push(widgetToUpdateInDashboard);
        }
        this.addWidgetDefaults(this.model.currentDashboard);
        this.validateChangesMadeToCurrentDashboard();
        this._currentDashboardUpdated$.next();
    }

    validateChangesMadeToCurrentDashboard(): void {
        this.model.areChangesMadeToCurrentDashboard = !DifferenceLogicUtility.valuesAreTheSame(this.model.currentDashboard!, this.model.currentDashboardBeforeEditState!);
    }

    addWidgetTypeSizeDefault(widgetToUpdateInDashboard: Widget): void {
        switch (widgetToUpdateInDashboard.type) {
            case WidgetType.Map:
                widgetToUpdateInDashboard.w = WIDGET_SIZES.MAP_WIDTH;
                widgetToUpdateInDashboard.h = WIDGET_SIZES.MAP_HEIGHT;
                break;
            case WidgetType.Report:
                widgetToUpdateInDashboard.w = WIDGET_SIZES.REPORT_WIDTH;
                widgetToUpdateInDashboard.h = WIDGET_SIZES.REPORT_HEIGHT;
                break;
            case WidgetType.ProjectNote:
                widgetToUpdateInDashboard.w = WIDGET_SIZES.PROJECT_NOTES_WIDTH;
                widgetToUpdateInDashboard.h = WIDGET_SIZES.PROJECT_NOTES_HEIGHT;
                break;
            case WidgetType.Pie:
            case WidgetType.Donut:
                widgetToUpdateInDashboard.w = WIDGET_SIZES.SEGMENT_CIRCULAR_CHART_WIDTH;
                widgetToUpdateInDashboard.h = WIDGET_SIZES.SEGMENT_CIRCULAR_CHART_HEIGHT;
                break;
            case WidgetType.Number:
            default:
                widgetToUpdateInDashboard.w = WIDGET_SIZES.NUMBER_WIDTH;
                widgetToUpdateInDashboard.h = WIDGET_SIZES.NUMBER_HEIGHT;
                break;
        }
    }

    setSelectedRoleIds(selectedRoleIds: string[]): void {
        this.model.dashboardForm.get('permissionRoleIds')?.setValue(selectedRoleIds);
    }

    setDashboardInEditMode(isInEditMode: boolean): void {
        if (isInEditMode) {
            this.setCurrentDashboardBeforeEditState();
        }
        else {
            this.resetCurrentDashboardToBeforeEditState();
            this.resetChangesMadeToCurrentDashboard();
        }

        this.model.isDashboardInEditMode = isInEditMode;
        this._currentDashboardUpdated$.next();
    }

    setWidgetInExpandedView(widget: Widget, currentScrollPosition: number, widgetSourceTooltip: string, widgetTooltip: string): void {
        this.model.widgetInExpandedView = widget;
        this.model.isWidgetExpanded = true;
        this.model.collapsedWidgetScrollPosition = currentScrollPosition;
        this.model.expandedWidgetSourceTooltip = widgetSourceTooltip;
        this.model.expandedWidgetTooltip = widgetTooltip;
    }

    collapseExpandedWidget(): void {
        this.model.widgetInExpandedView = undefined;
        this.model.isWidgetExpanded = false;
        this.model.expandedWidgetSourceTooltip = '';
        this.model.expandedWidgetTooltip = '';
    }

    clearCurrentScrollPosition(): void {
        this.model.collapsedWidgetScrollPosition = undefined;
    }

    resetChangesMadeToCurrentDashboard(): void {
        this.model.areChangesMadeToCurrentDashboard = false;
    }

    setCurrentDashboardBeforeEditState(): void {
        this.model.currentDashboardBeforeEditState = this.model.currentDashboard ? cloneDeep(this.model.currentDashboard) : undefined;
    }

    resetCurrentDashboardToBeforeEditState(): void {
        if (this.model.currentDashboardBeforeEditState) {
            const index = this.model.dashboards.findIndex(d => d.id === this.model.currentDashboardBeforeEditState?.id);
            this.updateLocalDashboards({ ...this.model.currentDashboardBeforeEditState });
            if (index !== -1) {
                this.setCurrentDashboard(this.model.dashboards[index].id);
            }
        }
    }

    private syncAllRolesSelectionState(projectRoles: Role[]): void {
        const selected = new Set(this.selectedRoleIds);
        const specificRoleIds = projectRoles
            .map(role => String(role.id))
            .filter(id => id !== AllSelection.ALL);

        if (specificRoleIds.length && specificRoleIds.every(id => selected.has(id))) {
            selected.add(AllSelection.ALL);
            this.setSelectedRoleIds(Array.from(selected));
        }
    }

    private addWidgetDefaults(dashboard: Dashboard): Dashboard {
        if (dashboard.widgets) {
            dashboard.widgets = dashboard.widgets.map(w => ({
                ...w,
                minH: this.minimumWidgetHeightBasedOnWidgetType(w),
                maxH: WIDGET_SIZES.MAX_HEIGHT,
                minW: w.type === WidgetType.Map ? WIDGET_SIZES.MAP_MIN_WIDTH : WIDGET_SIZES.MIN_WIDTH,
            }));
        }
        return dashboard;
    }

    minimumWidgetHeightBasedOnWidgetType(widget: Widget): number {
        switch (widget.type) {
            case WidgetType.Map:
                return WIDGET_SIZES.MAP_MIN_HEIGHT;
            case WidgetType.Number:
                return WIDGET_SIZES.NUMBER_MIN_HEIGHT;
            default:
                return WIDGET_SIZES.MIN_HEIGHT;
        }
    }

    setSelectedReportIds(selectedIds: string[]): void {
        const ids = selectedIds.filter(id => id !== AllSelection.ALL);
        this.model.widgetForm.get('columnIds')?.setValue(ids);
    }

    setSelectedProjectNoteIds(selectedIds: string[]): void {
        const ids = selectedIds.filter(id => id !== AllSelection.ALL);
        this.model.widgetForm.get('projectNoteIds')?.setValue(ids);
    }

    addMetricRule(): void {
        this.widgetMetricRulesFormArray.push(this.buildMetricRuleGroup());
    }

    retrieveReportIdsForCurrentDashboard(): Set<string> {
        return new Set(this.model.currentDashboard?.widgets?.map(w => w.reportId).filter((id): id is string => !!id) ?? []);
    }

    setCurrentProjectIdForLoadedSiteMapPins(projectId?: string): void {
        this.model.loadedSiteMapPinsProjectId = projectId ?? undefined;
    }

    setMapWidgetCurrentLatLongBounds(bounds: google.maps.LatLngBounds): void {
        this.model.mapWidgetCurrentLatLongBounds = bounds;
    }

    private buildMetricRuleGroup(rule?: WidgetMetricRule): UntypedFormGroup {
        return new UntypedFormGroup({
            operator: new UntypedFormControl(rule?.operator ?? null),
            value: new UntypedFormControl(rule?.value ?? null),
            aggregate: new UntypedFormControl(rule?.aggregate ?? null),
            gridAggregation: new UntypedFormControl(rule?.gridAggregation || null),
        });
    }

    private setProjectNoteWidgetValidators(): void {
        const projectNoteIds = this.model.widgetForm.get('projectNoteIds');

        projectNoteIds?.setValidators([Validators.required]);

        projectNoteIds?.updateValueAndValidity();
    }

    private isSingleSelectColumnWidget(type: string): boolean {
        return type !== WidgetType.Report;
    }

    stripStaleColumnIds(allFields: Field[] | undefined): void {
        if (!allFields) {
            return;
        }
        const currentValue = this.model.widgetForm.get('columnIds')?.value;
        if (!Array.isArray(currentValue) || currentValue.length === 0) {
            return;
        }
        const validIds = new Set(
            allFields?.map(f => f.id)
                .filter((id): id is string => !!id && id !== AllSelection.ALL)
        );
        const cleanedIds = (currentValue as string[]).filter(id => validIds.has(id));
        if (cleanedIds.length !== currentValue.length) {
            this.setSelectedReportIds(cleanedIds);
        }
    }

    private updatedDashboardWithFormValues(dashboardFromForm: Dashboard): Dashboard | null {
        const dashboardToUpdate = cloneDeep(this.model.dashboards.find(d => d.id === dashboardFromForm.id));
        if (!dashboardToUpdate) {
            return null;
        }
        dashboardToUpdate.name = dashboardFromForm.name;
        dashboardToUpdate.description = dashboardFromForm.description;
        dashboardToUpdate.permissionRoleIds = dashboardFromForm.permissionRoleIds;
        dashboardToUpdate.permissionUserIds = dashboardFromForm.permissionUserIds;
        dashboardToUpdate.allowAllUsersAccess = dashboardFromForm.allowAllUsersAccess;
        return dashboardToUpdate;
    }
}
